// Central knobs for on-device scan throughput. Pure decision functions (take
// their inputs) so they're unit-testable, with thin browser wrappers that read
// the real globals + overrides. Nothing here changes detection MATH — only how
// many workers run, which execution provider they use, and whether WASM can
// thread. Parity is unaffected by all of these EXCEPT WebGPU (a different numeric
// backend), which is why it stays behind a flag until the browser parity gate
// greens on a real GPU.

// ---- WebGPU -------------------------------------------------------------
// We ATTEMPT WebGPU (GPU-accelerated inference) whenever the device exposes one —
// DEFAULT ON when navigator.gpu is present, for the large speedup on desktop. This
// is safe because the worker now has TWO fallback layers: createSession falls back
// to WASM if a WebGPU session won't even build, AND (the fix for the "scans on
// mobile but not desktop" bug) scanWorker.demoteToWasm rebuilds on WASM + retries
// if a session builds but then throws/hangs at run() inside the worker. So a WebGPU
// failure degrades to a correct WASM scan instead of silently no-opping.
// WebGPU is a different numeric backend from WASM; if a device's results ever look
// off, turn it off per-deployment with VITE_SCAN_WEBGPU=false or per-tester with
// localStorage ph_scan_webgpu=0. `hasGpu` = navigator.gpu present.
export function resolveWebGpu({ envFlag, lsFlag, hasGpu } = {}) {
  if (!hasGpu) return false;
  // Explicit OFF switch always wins.
  const off =
    envFlag === false || envFlag === 'false' || envFlag === '0' ||
    lsFlag === '0' || lsFlag === 'false';
  if (off) return false;
  // Default ON when a GPU is present. WebGPU's known silent no-op (builds + run()s but
  // returns EMPTY output) is now caught by scanWorker's one-time FIRST-FILE check: it
  // re-runs the first file on WASM and permanently demotes the worker if WASM finds
  // detections WebGPU missed. So the throughput win is safe again. Force off per deploy
  // with VITE_SCAN_WEBGPU=false or per tester with localStorage ph_scan_webgpu=0.
  return true;
}

// ---- Worker pool size ---------------------------------------------------
// More workers = more inference in flight = scanning keeps up with a fast upload
// queue. Sizing balances against how each worker itself uses cores:
//   • webgpu:            GPU-bound — a small pool (2) overlaps decode + submit.
//   • cross-origin isol: each worker's WASM is multi-threaded, so few workers.
//   • single-threaded:   more workers, each pinned to one core.
export function scanPoolSize({ hardwareConcurrency, crossOriginIsolated, webgpu, override } = {}) {
  const ovr = Number(override);
  if (Number.isFinite(ovr) && ovr >= 1) return Math.min(8, Math.floor(ovr));
  const hc = Math.max(1, hardwareConcurrency || 4);
  if (webgpu) return 2;
  if (crossOriginIsolated) return Math.max(1, Math.min(2, Math.floor(hc / 4)));
  return Math.max(1, Math.min(4, Math.floor(hc / 2)));
}

// ---- WASM thread count (per worker) ------------------------------------
// onnxruntime-web can only use SharedArrayBuffer-backed threads when the page is
// cross-origin isolated (needs COOP/COEP — see server.mjs SCAN_COI). Without it
// this is 1 (harmless, today's behaviour). Threaded math is identical to single
// -threaded, just parallel — no parity impact.
export function wasmThreadCount({ crossOriginIsolated, hardwareConcurrency, poolSize } = {}) {
  if (!crossOriginIsolated) return 1;
  const hc = Math.max(1, hardwareConcurrency || 4);
  // Share cores across the pool so N workers × threads doesn't oversubscribe.
  const per = Math.floor(hc / Math.max(1, poolSize || 1));
  return Math.max(1, Math.min(8, per));
}

// ---- Browser wrappers ---------------------------------------------------
const ls = (key) => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null; // localStorage can throw in sandboxed/embedded contexts
  }
};

export function webGpuEnabled() {
  return resolveWebGpu({
    envFlag: import.meta.env?.VITE_SCAN_WEBGPU,
    lsFlag: ls('ph_scan_webgpu'),
    hasGpu: typeof navigator !== 'undefined' && !!navigator.gpu,
  });
}

// ---- WebGPU OCR trial (default OFF) ------------------------------------
// PaddleOCR (text) normally runs on WASM — the numeric backend the Phase-0 text
// parity gate validated and the one the server trusts (Phase-2 hatch). This flag
// lets a tester run OCR on WebGPU to A/B its speed AND output against WASM. It is
// a DIFFERENT numeric backend, so it must pass a text parity gate before it could
// ever be the default. Per-tester only: localStorage ph_scan_ocr_webgpu === '1'.
export function ocrWebGpuEnabled() {
  return ls('ph_scan_ocr_webgpu') === '1';
}

export function poolSize() {
  return scanPoolSize({
    hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined,
    crossOriginIsolated: typeof globalThis !== 'undefined' && globalThis.crossOriginIsolated === true,
    webgpu: webGpuEnabled(),
    override: ls('ph_scan_pool'),
  });
}
